import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Button, LoadingSkeleton, Modal, ToastContainer } from './ui';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { safeErrorMessage } from '../utils/apiError';
import { hasAnyRole } from '../utils/roles';

const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

const CREATE_ROLES = ['administrator', 'principal', 'head_of_academics', 'teacher'];
const DELETE_ROLES = ['administrator', 'principal', 'head_of_academics'];

const EMPTY_FORM = { class_id: '', subject_allocation_id: '', day_of_week: 'Monday', start_time: '08:00', end_time: '09:30', room: '' };

function Timetable({ token, user }) {
  const canCreate = hasAnyRole(user, CREATE_ROLES);
  const canDelete = hasAnyRole(user, DELETE_ROLES);
  const [timetable, setTimetable] = useState([]);
  const [classes, setClasses] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [formData, setFormData] = useState(EMPTY_FORM);

  const addToast = (message, type = 'success') => setToasts(prev => [...prev, { message, type }]);
  const removeToast = (index) => setToasts(prev => prev.filter((_, i) => i !== index));

  const fetchData = useCallback(async () => {
    try {
      const [timetableRes, classesRes] = await Promise.all([
        fetch('/api/v1/timetable', { headers: { Authorization: `Bearer ${token}` } }),
        fetch('/api/v1/classes', { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setTimetable(await timetableRes.json());
      setClasses(await classesRes.json());
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleClassChange = async (classId) => {
    setFormData({ ...formData, class_id: classId, subject_allocation_id: '' });
    if (!classId) { setAllocations([]); return; }
    try {
      const response = await fetch(`/api/v1/subject-allocations?class_id=${classId}`, { headers: { Authorization: `Bearer ${token}` } });
      setAllocations(response.ok ? await response.json() : []);
    } catch {
      setAllocations([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch('/api/v1/timetable', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          subject_allocation_id: formData.subject_allocation_id,
          day_of_week: formData.day_of_week,
          start_time: formData.start_time,
          end_time: formData.end_time,
          room: formData.room,
        }),
      });
      if (response.ok) {
        setShowForm(false);
        fetchData();
        setFormData(EMPTY_FORM);
        setAllocations([]);
      } else if (response.status === 409) {
        const data = await response.json().catch(() => ({}));
        const conflictTypes = [...new Set((data.conflicts || []).map(c => c.type))].join(', ');
        addToast(`${data.error || 'Clash detected'}${conflictTypes ? ` (${conflictTypes})` : ''}`, 'error');
      } else {
        addToast(await safeErrorMessage(response, 'Failed to add timetable entry'), 'error');
      }
    } catch (error) {
      console.error('Error:', error);
      addToast('Network error. Please check your connection and try again.', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      const response = await fetch(`/api/v1/timetable/${id}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) fetchData();
      else addToast(await safeErrorMessage(response, 'Failed to delete entry'), 'error');
    } catch (error) {
      console.error('Error:', error);
      addToast('Network error. Please check your connection and try again.', 'error');
    }
  };

  const columns = [
    { header: 'Class', accessor: 'class_name' },
    { header: 'Subject', accessor: 'subject' },
    { header: 'Teacher', accessor: 'teacher' },
    { header: 'Day', accessor: 'day_of_week' },
    { header: 'Time', render: (row) => `${row.start_time} - ${row.end_time}` },
    { header: 'Room', render: (row) => row.room || '—' },
  ];

  if (canDelete) {
    columns.push({
      header: 'Actions', width: '80px',
      render: (row) => (
        <Button variant="danger" size="sm" onClick={() => handleDelete(row.id)}>
          <TrashIcon style={{ width: 14, height: 14 }} />
        </Button>
      ),
    });
  }

  if (loading) return <LoadingSkeleton type="text" rows={10} />;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--color-text)' }}>School Timetable</h2>
        {canCreate && (
          <Button variant="primary" size="sm" onClick={() => setShowForm(!showForm)}>
            <PlusIcon style={{ width: 16, height: 16 }} />
            {showForm ? 'Cancel' : 'Add Entry'}
          </Button>
        )}
      </div>

      <Modal isOpen={showForm} onClose={() => setShowForm(false)} title="Add Timetable Entry">
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label>Class *</label>
              <select value={formData.class_id} onChange={(e) => handleClassChange(e.target.value)} required>
                <option value="">Select Class</option>
                {classes.map(c => <option key={c.id} value={c.id}>{c.display_name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Subject Allocation *</label>
              <select value={formData.subject_allocation_id} onChange={(e) => setFormData({ ...formData, subject_allocation_id: e.target.value })} required disabled={!formData.class_id}>
                <option value="">{formData.class_id ? 'Select Subject / Teacher' : 'Select a class first'}</option>
                {allocations.map(a => <option key={a.id} value={a.id}>{a.subject_name} — {a.teacher_name}</option>)}
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Day *</label>
              <select value={formData.day_of_week} onChange={(e) => setFormData({ ...formData, day_of_week: e.target.value })} required>
                {days.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Room</label>
              <input type="text" value={formData.room} onChange={(e) => setFormData({ ...formData, room: e.target.value })} placeholder="e.g., Room 101" />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Start Time *</label>
              <input type="time" value={formData.start_time} onChange={(e) => setFormData({ ...formData, start_time: e.target.value })} required />
            </div>
            <div className="form-group">
              <label>End Time *</label>
              <input type="time" value={formData.end_time} onChange={(e) => setFormData({ ...formData, end_time: e.target.value })} required />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
            <Button type="submit" variant="primary">Add Entry</Button>
            <Button type="button" variant="secondary" onClick={() => setShowForm(false)}>Cancel</Button>
          </div>
        </form>
      </Modal>

      <Card title="Timetable">
        <Table columns={columns} data={timetable} emptyMessage="No timetable entries found." />
      </Card>

      <ToastContainer toasts={toasts} onClose={removeToast} />
    </div>
  );
}

export default Timetable;
